import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi, api } from '@/lib/api-client';
import type { PaginatedResponse } from '@supernova/shared';

export function useProducts(
  params: Record<string, string | number | boolean | undefined> = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => productsApi.get<PaginatedResponse<any>>('/products', params),
    enabled: options.enabled ?? true,
    // Refetch when the user returns to the tab so edits made in another tab
    // (e.g. setting case → 5 on the product edit page) flow into long-lived
    // forms like the PO create flow without a manual reload.
    refetchOnWindowFocus: true,
  });
}

/** Distinct product `building` values in use — options for the All Products
 *  "Building Type" filter. Takes the same locationId / isActive scope as the
 *  list so it never offers a building that would match nothing. */
export function useProductBuildings(params: Record<string, string | boolean | undefined> = {}) {
  return useQuery({
    queryKey: ['products', 'buildings', params],
    queryFn: () => productsApi.get<{ data: string[] }>('/products/buildings', params),
  });
}

export function useProduct(id: string | undefined, locationId?: string) {
  return useQuery({
    queryKey: ['products', id, locationId || null],
    queryFn: () => productsApi.get<{ data: any }>(`/products/${id}`, locationId ? { locationId } : undefined),
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => productsApi.post('/products', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => productsApi.put(`/products/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// Inline per-location selling-price update for the product list. Hits the
// products service's targeted `location-inventory` endpoint, which upserts
// ONLY product_prices.sellingPrice for (product, location) — leaving cost,
// units and every other field untouched. The full PUT /products/:id path is
// wrong here: without a location it rewrites every location's price, and its
// `prices` array would null out the location's cost we don't carry in the row.
export function useUpdateProductPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      locationId,
      sellingPrice,
    }: {
      productId: string;
      locationId: string;
      sellingPrice: number;
    }) =>
      productsApi.put(`/products/${productId}/location-inventory`, {
        locationId,
        sellingPrice,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeactivateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productsApi.post(`/products/${id}/deactivate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useRestoreProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productsApi.post(`/products/${id}/restore`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useProductPriceHistory(
  productId: string | undefined,
  params: Record<string, string | number | undefined> = {},
) {
  // Hit the products service directly — it expands unit/case-row changes and
  // per-location overrides into rows with `unitName` and `locationId`, which
  // the dashboard's own price_history table doesn't carry. Without this, the
  // UI sees every scalar product price change at every location and can't
  // distinguish warehouse case-row updates from store retail edits.
  return useQuery({
    queryKey: ['products', productId, 'price-history', params],
    queryFn: () => productsApi.get<PaginatedResponse<any>>(`/products/${productId}/price-history`, params),
    enabled: !!productId,
  });
}

export function useProductAuditLog(
  productId: string | undefined,
  params: Record<string, string | number | undefined> = {},
) {
  return useQuery({
    queryKey: ['products', productId, 'audit-log', params],
    queryFn: () => productsApi.get<PaginatedResponse<any>>(`/products/${productId}/audit-log`, params),
    enabled: !!productId,
  });
}

/** Flip a product's "Add to PO" flag for one location — the same call
 *  Supernova Mobile's Find Product toggle makes, so both platforms read and
 *  write one `product_locations.force_include_auto_po` value. */
export function useToggleProductPo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      locationId,
      forceIncludeAutoPo,
    }: {
      productId: string;
      locationId: string;
      forceIncludeAutoPo: boolean;
    }) => productsApi.put(`/products/${productId}/location-inventory`, { locationId, forceIncludeAutoPo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // The PO product picker's "Force include" badge reads the same flag.
      queryClient.invalidateQueries({ queryKey: ['payee-products'] });
    },
  });
}

export function useRecordPriceChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      changes,
    }: {
      productId: string;
      changes: Array<{ priceType: string; oldValue: number | null; newValue: number | null }>;
    }) => api.post(`/products/${productId}/price-history`, { changes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useVendorPriceHistory(
  productId: string | undefined,
  params: Record<string, string | number | undefined> = {},
) {
  return useQuery({
    queryKey: ['products', productId, 'vendor-price-history', params],
    queryFn: () => productsApi.get<PaginatedResponse<any>>(`/products/${productId}/vendor-price-history`, params),
    enabled: !!productId,
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productsApi.delete(`/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useBulkImportProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { products: any[]; locationId?: string }) =>
      productsApi.post<{
        inserted: number;
        updated: number;
        departmentsCreated: number;
        categoriesCreated: number;
        vendorsCreated: number;
        errors: string[];
      }>('/products/bulk-import', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['sp-payees'] });
    },
  });
}

/**
 * Stock + selling price of this product at other tenants' private warehouses
 * mapped (by super admin) to the given store locations. Read-only.
 */
export function usePrivateWarehouseStock(productId: string | undefined, locationIds: string[]) {
  const key = [...locationIds].sort().join(',');
  return useQuery({
    queryKey: ['private-warehouse-stock', productId, key],
    queryFn: () =>
      api.get<{
        data: {
          warehouseLocationId: string;
          warehouseName: string;
          ownerTenantName: string;
          mapped: boolean;
          inStockQty: number | null;
          unit: string | null;
          sellingPrice: number | null;
          priceHidden: boolean;
        }[];
      }>(`/products/${productId}/private-warehouse-stock`, { locationIds: key }),
    enabled: !!productId && locationIds.length > 0,
  });
}
